/**
 * Self-scoped reads — GET /me/attendance and GET /me/logs.
 *
 * These go through requestMe(), which carries the company as the X-Company-Id
 * header. That header is a FILTER here, not an identity: sv_gateway's /me handlers
 * (sv_gateway/src/handlers/me.rs) take the caller from the JWT and read the company
 * from the header (extract_company_id). So the query string carries only month / page,
 * and `companyId` rides as requestMe's second argument — duplicating company_id into
 * the query would be dead weight the server ignores.
 *
 * A caller not linked to a personnel record in that company gets a 404 from the
 * server — that is expected, and the screen renders it as "you are not an employee
 * here", never a crash.
 */
import {requestMe} from '@/shared/api';
import {
  collectDay,
  dayWindow,
  type DayLog,
  type DayWindow,
} from '@/entities/log/day';
import type {PagedLogs} from '@/entities/log/types';
import type {PersonnelAttendanceReport} from '@/entities/attendance/types';
import {
  periodParams,
  requestPeriod,
  type Period,
} from '@/entities/attendance/period';

/**
 * My own attendance report for any period the employer-side reports accept — the route
 * takes the identical `?month=` / `?from=&to=` pair, so an employee is not restricted
 * to one calendar month at a time just because that is all the first version asked for.
 */
export function fetchMyAttendance(
  companyId: string,
  period: Period,
): Promise<PersonnelAttendanceReport> {
  // `requestPeriod`, а не `clampPeriod`: экран сотрудника открывается на ТЕКУЩЕМ месяце,
  // то есть это ровно то место, где будущие дни месяца приезжали красными клетками в
  // календаре собственной явки.
  const qs = new URLSearchParams(periodParams(requestPeriod(period)));
  return requestMe<PersonnelAttendanceReport>(
    `/me/attendance?${qs.toString()}`,
    companyId,
  );
}

/** My own access events (1-based page). */
export function fetchMyLogs(
  companyId: string,
  opts: {page: number; perPage: number},
): Promise<PagedLogs> {
  const qs = new URLSearchParams({
    page: String(opts.page),
    per_page: String(opts.perPage),
  });
  return requestMe<PagedLogs>(`/me/logs?${qs.toString()}`, companyId);
}

/**
 * Мои проходы за ОДИН день.
 *
 * Тот же сбор на клиенте, что и у работодателя ({@link fetchPersonDayLogs}), и по той
 * же причине: даты маршрут не принимает — только страницу. Фильтровать по человеку
 * здесь не нужно и нечем: `/me/logs` уже отдаёт события одного личного дела —
 * `record_id` не принимается ни в запросе, ни в теле ни на одном маршруте семьи `/me`.
 */
export function fetchMyDayLogs(
  companyId: string,
  date: string,
): Promise<DayLog> {
  return collectDay(dayWindow(date), (index, size) =>
    fetchMyLogs(companyId, {page: index + 1, perPage: size}),
  );
}

/**
 * Мои проходы за ПРОИЗВОЛЬНЫЙ отрезок — тот же сбор, только окно шире.
 *
 * Нужен по той же причине, что и у работодателя: `/me/logs` не принимает ни дат, ни
 * результата, ни порядка — только страницу. Значит и период, и отбор «только отказы»,
 * и сортировка возможны лишь по СОБРАННОМУ; отфильтровать одну серверную страницу и
 * подписать её периодом значило бы соврать.
 *
 * Личное дело маленькое — сотни событий в год против десятков тысяч у компании, —
 * поэтому бюджет страниц здесь дефолтный: месяц собирается за один-два запроса.
 */
export function fetchMyLogsInRange(
  companyId: string,
  window: DayWindow,
): Promise<DayLog> {
  return collectDay(window, (index, size) =>
    fetchMyLogs(companyId, {page: index + 1, perPage: size}),
  );
}
